import React, { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { ArrowRight, ChevronDown, LayoutGrid } from 'lucide-react';
import { datasetApi } from '../../../api/datasetApi';
import { cn } from '../../../lib/utils';
import {
  OVERVIEW_CHIP_CLASS,
  OVERVIEW_CHIP_VALUE_CLASS,
} from './datasetOverview';

const formatDate = (dateStr) => {
  if (!dateStr) return null;
  try {
    return new Date(dateStr).toLocaleDateString(undefined, {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
    });
  } catch {
    return null;
  }
};

const workspaceLabel = (count) => {
  if (count === 1) return 'Used in 1 workspace';
  return `Used in ${count} workspaces`;
};

const DatasetWorkspacesExpandable = ({ dataset, onNavigate, children }) => {
  const [workspaces, setWorkspaces] = useState([]);
  const [isLoading, setIsLoading] = useState(() => Boolean(dataset?.id));
  const [isOpen, setIsOpen] = useState(false);
  const [error, setError] = useState(null);
  const [hasFetched, setHasFetched] = useState(false);
  const menuRef = useRef(null);

  useEffect(() => {
    if (!dataset?.id) {
      setWorkspaces([]);
      setError(null);
      setHasFetched(false);
      setIsOpen(false);
      setIsLoading(false);
      return;
    }

    let cancelled = false;

    setWorkspaces([]);
    setError(null);
    setHasFetched(false);
    setIsOpen(false);
    setIsLoading(true);

    datasetApi
      .getDatasetWorkspaces(dataset.id)
      .then((data) => {
        if (cancelled) return;
        setWorkspaces(data || []);
        setError(null);
      })
      .catch((err) => {
        if (cancelled) return;
        console.error('Failed to fetch dataset workspaces:', err);
        setWorkspaces([]);
        setError('Could not load workspaces.');
      })
      .finally(() => {
        if (cancelled) return;
        setIsLoading(false);
        setHasFetched(true);
      });

    return () => {
      cancelled = true;
    };
  }, [dataset?.id]);

  useEffect(() => {
    if (!isOpen) return;

    const handlePointerDown = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setIsOpen(false);
      }
    };

    const handleKeyDown = (event) => {
      if (event.key === 'Escape') setIsOpen(false);
    };

    document.addEventListener('mousedown', handlePointerDown);
    document.addEventListener('keydown', handleKeyDown);
    return () => {
      document.removeEventListener('mousedown', handlePointerDown);
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [isOpen]);

  let workspaceChip;
  if (isLoading) {
    workspaceChip = (
      <div className={cn(OVERVIEW_CHIP_CLASS, 'text-gray-400')}>
        <span className={OVERVIEW_CHIP_VALUE_CLASS}>Loading workspaces...</span>
      </div>
    );
  } else if (error) {
    workspaceChip = (
      <div className={cn(OVERVIEW_CHIP_CLASS, 'border-red-200 text-red-500 dark:border-red-900/50')}>
        <span className={OVERVIEW_CHIP_VALUE_CLASS}>{error}</span>
      </div>
    );
  } else if (hasFetched && workspaces.length === 0) {
    workspaceChip = (
      <div className={cn(OVERVIEW_CHIP_CLASS, 'text-gray-400')}>
        <LayoutGrid size={12} className="shrink-0" aria-hidden />
        <span className={OVERVIEW_CHIP_VALUE_CLASS}>Not used in any workspace</span>
      </div>
    );
  } else if (hasFetched && workspaces.length > 0) {
    workspaceChip = (
      <div className="relative" ref={menuRef}>
        <button
          type="button"
          onClick={() => setIsOpen((open) => !open)}
          aria-expanded={isOpen}
          aria-haspopup="listbox"
          className={cn(
            OVERVIEW_CHIP_CLASS,
            'transition-colors hover:border-emerald-200 hover:bg-emerald-50 dark:hover:border-emerald-800 dark:hover:bg-emerald-900/20',
            isOpen && 'border-emerald-300 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/20'
          )}
        >
          <LayoutGrid size={12} className="shrink-0 text-emerald-600 dark:text-emerald-400" aria-hidden />
          <span className={cn(OVERVIEW_CHIP_VALUE_CLASS, 'text-emerald-700 dark:text-emerald-300')}>
            {workspaceLabel(workspaces.length)}
          </span>
          <ChevronDown
            size={12}
            className={cn('shrink-0 text-emerald-500 transition-transform', isOpen && 'rotate-180')}
          />
        </button>

        {isOpen ? (
          <ul
            role="listbox"
            className="absolute left-0 top-full z-50 mt-1.5 min-w-[280px] max-h-64 overflow-y-auto rounded-xl border border-gray-200 bg-white py-1 shadow-xl custom-scrollbar dark:border-gray-700 dark:bg-gray-950"
          >
            {workspaces.map((workspace) => {
              const updatedLabel = formatDate(workspace.updated_at || workspace.created_at);

              return (
                <li
                  key={workspace.id}
                  className="border-b border-gray-50 last:border-b-0 dark:border-gray-900/50"
                >
                  <Link
                    to={`/workspace/${workspace.id}`}
                    onClick={() => {
                      setIsOpen(false);
                      onNavigate?.();
                    }}
                    className="group flex items-center justify-between gap-3 px-4 py-3 transition-colors hover:bg-gray-50/80 dark:hover:bg-gray-900/40"
                  >
                    <div className="min-w-0">
                      <p className="truncate text-xs font-black uppercase tracking-tight text-gray-900 dark:text-white">
                        {workspace.name}
                      </p>
                      {workspace.description ? (
                        <p className="mt-0.5 truncate text-[11px] text-gray-500 dark:text-gray-400">
                          {workspace.description}
                        </p>
                      ) : null}
                    </div>
                    <div className="flex shrink-0 items-center gap-2">
                      {updatedLabel ? (
                        <span className="text-[9px] font-bold uppercase tracking-widest text-gray-400">
                          {updatedLabel}
                        </span>
                      ) : null}
                      <ArrowRight
                        size={12}
                        className="text-gray-300 transition-all group-hover:translate-x-0.5 group-hover:text-emerald-500"
                      />
                    </div>
                  </Link>
                </li>
              );
            })}
          </ul>
        ) : null}
      </div>
    );
  }

  return (
    <div className="w-full shrink-0">
      <div className="flex flex-wrap items-center gap-2">
        {workspaceChip}
        {children}
      </div>
    </div>
  );
};

export default DatasetWorkspacesExpandable;
